import axiosInstance from '../../axiosInstance';
import {
  EpicChild,
  EpicChildrenPayload,
  EpicDetail,
  EpicList,
  EpicStatusFilter,
  EpicStatusPayload,
  EpicSummary,
  SetEpicPayload,
} from 'types/project/epic';
import { BusinessError, normalizeApiError } from 'utils/normalizeApiError';

const epicBase = (projectUuid: string, epicUuid: string) =>
  `/pmo/project/${projectUuid}/epic/${epicUuid}`;

// The backend can answer 200 with { response: { status: false, message } }.
const unwrap = <T>(data: { response: T }): T => {
  const response = data?.response as T & { status?: unknown; message?: string };
  if ((response as any)?.status === false) {
    throw new BusinessError(response.message || 'Something went wrong');
  }
  return response;
};

/** The project's Epics matching `status`, with counts of every Epic for the filter chips. */
export const getProjectEpics = async (
  projectUuid: string,
  status: EpicStatusFilter = 'ALL',
): Promise<EpicList> => {
  try {
    const { data } = await axiosInstance.get<{ response: Partial<EpicList> }>(
      `/pmo/project/${projectUuid}/epics`,
      { params: { status } },
    );
    const result = unwrap(data);
    return {
      epics: result?.epics ?? [],
      openEpicCount: result?.openEpicCount ?? 0,
      doneEpicCount: result?.doneEpicCount ?? 0,
      totalEpicCount: result?.totalEpicCount ?? 0,
    };
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/** An Epic with its counts and its children; `hideDone` leaves out done children. */
export const getEpic = async (
  projectUuid: string,
  epicUuid: string,
  hideDone: boolean,
): Promise<EpicDetail> => {
  try {
    const { data } = await axiosInstance.get<{ response: EpicDetail }>(epicBase(projectUuid, epicUuid), {
      params: { hideDone },
    });
    const result = unwrap(data);
    return { epic: result.epic, children: result.children ?? [] };
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/** Stories, Tasks and Bugs with no parent, which can be linked to the Epic. */
export const getLinkableEpicItems = async (
  projectUuid: string,
  epicUuid: string,
  search?: string,
): Promise<EpicChild[]> => {
  try {
    const { data } = await axiosInstance.get<{ response: EpicChild[] }>(
      `${epicBase(projectUuid, epicUuid)}/linkable-items`,
      { params: { search: search || undefined, size: 50 } },
    );
    return unwrap(data) ?? [];
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/** All or nothing: if one item can't be linked, none is. */
export const addEpicChildren = async ({
  projectUuid,
  epicUuid,
  workItemUuids,
}: EpicChildrenPayload): Promise<EpicSummary> => {
  try {
    const { data } = await axiosInstance.post<{ response: EpicSummary }>(
      `${epicBase(projectUuid, epicUuid)}/add-children`,
      { workItemUuids },
    );
    return unwrap(data);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Sets one Epic on several Stories, Tasks and Bugs, or takes them out of their
 * Epic when `epicUuid` is null. All or nothing; a Done Epic is reopened.
 */
export const setEpic = async ({ projectUuid, workItemUuids, epicUuid }: SetEpicPayload): Promise<number> => {
  try {
    const { data } = await axiosInstance.post<{ response: { status?: boolean; message?: string; updatedCount?: number } }>(
      `/pmo/project/${projectUuid}/work-items/set-epic`,
      { workItemUuids, epicUuid },
    );
    return unwrap(data)?.updatedCount ?? 0;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/** Moves the Epic to the first Done status. Its children don't change. */
export const markEpicDone = async ({ projectUuid, epicUuid }: EpicStatusPayload): Promise<EpicSummary> => {
  try {
    const { data } = await axiosInstance.post<{ response: EpicSummary }>(
      `${epicBase(projectUuid, epicUuid)}/mark-done`,
    );
    return unwrap(data);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/** Moves a done Epic to the first To Do status. */
export const reopenEpic = async ({ projectUuid, epicUuid }: EpicStatusPayload): Promise<EpicSummary> => {
  try {
    const { data } = await axiosInstance.post<{ response: EpicSummary }>(
      `${epicBase(projectUuid, epicUuid)}/reopen`,
    );
    return unwrap(data);
  } catch (error) {
    throw normalizeApiError(error);
  }
};
